import { useOutletContext } from "react-router";

import { useInfiniteQuery } from "@tanstack/react-query";

import { BellIcon, MessageSquareIcon, ShieldIcon, TicketIcon, UserMinusIcon, UserPlusIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { NotificationList } from "@/features/notifications/components/NotificationList";
import { NotificationSkeleton } from "@/features/notifications/components/NotificationSkeleton";
import { TYPE_CONFIG } from "@/features/notifications/config";
import { isUnread } from "@/features/notifications/helpers";
import { EmptyState } from "@/shared/components/EmptyState";
import Error from "@/shared/components/Error";
import { InlineError } from "@/shared/components/InlineError";
import { StatCard, StatCardSkeleton } from "@/shared/components/StatCard";
import { useErrorModal } from "@/shared/hooks/useErrorModal";
import { QUERY_EXPIRATIONS, QUERY_KEYS } from "@/shared/lib/constants/queries";
import { getMyNotifications } from "@/features/notifications/services/notifications";
import { ApiNotificationType } from "@/features/notifications/types";
import { AppError } from "@/shared/lib/utils/errors";

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function Notifications() {
  const { permissions } = useOutletContext() as { permissions: string[] };

  const { data, isPending, error, refetch, fetchNextPage, isFetchingNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_MY_NOTIFICATIONS],
    queryFn: ({ pageParam }) => getMyNotifications(pageParam),
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) => lastPage.nextStartingAfter,
    staleTime: QUERY_EXPIRATIONS.GET_MY_NOTIFICATIONS,
  });

  const { open: errorModalOpen, setOpen: setErrorModalOpen, handleRetry } = useErrorModal(error, refetch);

  const notifications = data?.pages.flatMap((p) => p.notifications) ?? [];
  const unread = notifications.filter(isUnread);
  const invites = notifications.filter((n) => n.type === "ProjectInvitation");
  const removals = notifications.filter((n) => n.type === "ProjectRemoval");
  const roleUpdates = notifications.filter((n) => n.type === "ProjectRoleUpdate");
  const ticketUpdates = notifications.filter((n) => n.type === "TicketAssignment" || n.type === "TicketUpdate");
  const commentReplies = notifications.filter((n) => n.type === "CommentReply");
  const pendingInvites = invites.filter((n) => n.invitationData?.invitationStatus === "Pending");

  const unreadCount = unread.length;
  const totalCount = notifications.length;

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="font-heading text-2xl font-semibold">Notifications</h1>
            {!isPending && unreadCount > 0 && <Badge className="text-xs px-2 py-0.5">{unreadCount} new</Badge>}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">Activity across all your projects and tickets.</p>
        </div>
      </div>

      {error && <Error variant="modal" title="Failed to load notifications" error={error as AppError | Error} open={errorModalOpen} onOpenChange={setErrorModalOpen} onRetry={handleRetry} />}

      {/* Summary stat cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        {isPending ? (
          Array.from({ length: 6 }).map((_, i) => <StatCardSkeleton key={i} />)
        ) : (
          <>
            <StatCard label="Unread" value={unreadCount} icon={BellIcon} iconClass="text-primary" bgClass="bg-primary/10" />
            <StatCard label="Invitations" value={pendingInvites.length} icon={UserPlusIcon} iconClass="text-primary" bgClass="bg-primary/10" />
            <StatCard label="Removals" value={removals.length} icon={UserMinusIcon} iconClass="text-destructive" bgClass="bg-destructive/10" />
            <StatCard label="Role updates" value={roleUpdates.length} icon={ShieldIcon} iconClass="text-amber-600 dark:text-amber-400" bgClass="bg-amber-500/10" />
            <StatCard label="Ticket activity" value={ticketUpdates.length} icon={TicketIcon} iconClass="text-primary" bgClass="bg-primary/10" />
            <StatCard label="Replies" value={commentReplies.length} icon={MessageSquareIcon} iconClass="text-primary" bgClass="bg-primary/10" />
          </>
        )}
      </div>

      {/* Tabbed notification list */}
      <Card className="p-0 gap-0">
        <Tabs defaultValue="all">
          <CardHeader className="px-4 py-3 border-b">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <TabsList className="h-full w-fit flex-row flex-wrap gap-1 bg-muted p-1">
                <TabsTrigger value="all" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none">
                  <BellIcon />
                  All
                  {!isPending && (
                    <Badge variant={null} className="ml-1 px-1.5 py-0 text-[10px]">
                      {totalCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="unread" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none">
                  Unread
                  {!isPending && unreadCount > 0 && (
                    <Badge variant={null} className="ml-1 size-4 justify-center p-0 text-[10px]">
                      {unreadCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="invites" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none">
                  <UserPlusIcon />
                  Invites
                  {!isPending && invites.length > 0 && (
                    <Badge variant={null} className="ml-1 px-1.5 py-0 text-[10px]">
                      {invites.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="removals" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none">
                  <UserMinusIcon />
                  Removals
                  {!isPending && removals.length > 0 && (
                    <Badge variant={null} className="ml-1 px-1.5 py-0 text-[10px]">
                      {removals.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="role-updates" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none">
                  <ShieldIcon />
                  Role updates
                  {!isPending && roleUpdates.length > 0 && (
                    <Badge variant={null} className="ml-1 px-1.5 py-0 text-[10px]">
                      {roleUpdates.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="ticket-activity" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none">
                  <TicketIcon />
                  Ticket activity
                  {!isPending && ticketUpdates.length > 0 && (
                    <Badge variant={null} className="ml-1 px-1.5 py-0 text-[10px]">
                      {ticketUpdates.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="replies" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none">
                  <MessageSquareIcon />
                  Replies
                  {!isPending && commentReplies.length > 0 && (
                    <Badge variant={null} className="ml-1 px-1.5 py-0 text-[10px]">
                      {commentReplies.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              {!isPending && unreadCount > 0 && (
                <p className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">{unreadCount}</span> unread
                </p>
              )}
            </div>
          </CardHeader>

          {error && !errorModalOpen ? (
            <InlineError title="Failed to load notifications" description="Something went wrong while fetching your notifications." onRetry={handleRetry} />
          ) : isPending ? (
            <div className="flex flex-col divide-y">
              {Array.from({ length: 5 }).map((_, i) => (
                <NotificationSkeleton key={i} />
              ))}
            </div>
          ) : (
            <>
              <TabsContent value="all" className="mt-0">
                {totalCount === 0 ? <EmptyState icon={BellIcon} title="You're all caught up" description="New notifications will appear here when activity happens." /> : <NotificationList notifications={notifications} permissions={permissions} />}
              </TabsContent>
              <TabsContent value="unread" className="mt-0">
                {unread.length === 0 ? <EmptyState icon={BellIcon} title="You're all caught up" description="New notifications will appear here when activity happens." /> : <NotificationList notifications={unread} permissions={permissions} />}
              </TabsContent>
              <TabsContent value="invites" className="mt-0">
                <NotificationList notifications={invites} permissions={permissions} />
              </TabsContent>
              <TabsContent value="removals" className="mt-0">
                <NotificationList notifications={removals} permissions={permissions} />
              </TabsContent>
              <TabsContent value="role-updates" className="mt-0">
                <NotificationList notifications={roleUpdates} permissions={permissions} />
              </TabsContent>
              <TabsContent value="ticket-activity" className="mt-0">
                <NotificationList notifications={ticketUpdates} permissions={permissions} />
              </TabsContent>
              <TabsContent value="replies" className="mt-0">
                {commentReplies.length === 0 ? <EmptyState icon={MessageSquareIcon} title="No replies yet" description="You'll be notified when someone replies to one of your comments." /> : <NotificationList notifications={commentReplies} permissions={permissions} />}
              </TabsContent>
            </>
          )}
        </Tabs>

        {!isPending && !error && totalCount > 0 && (
          <div className="border-t px-4 py-3 flex items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">{totalCount}</span> notification{totalCount !== 1 ? "s" : ""}
              {unreadCount > 0 && (
                <>
                  {" · "}
                  <span className="font-semibold text-primary">{unreadCount} unread</span>
                </>
              )}
            </p>
            {hasNextPage && (
              <Button variant="outline" size="sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                {isFetchingNextPage ? "Loading..." : "Load more"}
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Notification type reference card */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <BellIcon className="size-4 text-primary" />
            <CardTitle>Notification types</CardTitle>
          </div>
          <CardDescription>The events that trigger notifications in your account.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6">
            {(["ProjectInvitation", "ProjectRemoval", "ProjectRoleUpdate", "TicketAssignment", "TicketUpdate", "CommentReply"] as ApiNotificationType[]).map((type) => {
              const cfg = TYPE_CONFIG[type];
              const Icon = cfg.icon;
              const descriptions: Record<ApiNotificationType, string> = {
                ProjectInvitation: "Sent when another member invites you to join a project. You can accept or decline directly from the notification.",
                ProjectRemoval: "Sent when an admin removes you from a project. Your access is revoked immediately.",
                ProjectRoleUpdate: "Sent when an admin changes your role within a project.",
                TicketAssignment: "Sent when another member assigns you to a ticket. You won't receive this for tickets you assign to yourself.",
                TicketUpdate: "Sent when a ticket you're assigned to is updated by another member. You won't receive this for changes you make yourself.",
                CommentReply: "Sent when another member replies to a comment you left on a ticket. You won't receive this for replies to your own comments.",
              };
              return (
                <div key={type} className="flex flex-col gap-3 p-5">
                  <div className="flex items-center gap-2.5">
                    <div className={`flex size-7 items-center justify-center ${cfg.bgClass}`}>
                      <Icon className={`size-3.5 ${cfg.iconClass}`} />
                    </div>
                    <span className="text-xs font-semibold tracking-wider uppercase">{cfg.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{descriptions[type]}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
